// Lịch hẹn ↔ POS: lịch hẹn khách đã "Xác nhận làm" → danh sách chờ thu ngân; đối chiếu đơn POS với lịch hẹn
import { useCallback, useEffect, useRef, useState } from 'react';
import { getPosWaitingOrders } from 'api/PosV2';
import type { AppointmentLine, AppointmentSnapshot, CartLine, WaitingAppointment } from './data';
import { toWaiting } from './remote';

/** customer_tag_status của nhóm "Chờ thực hiện" (S7 Xác nhận làm, S7.1 Đã cọc, S7.2 Đang điều trị) */
export const CONFIRMED_TAG_STATUS = 3;
const POLL_MS = 30_000;

const num = (v: unknown) => Number(v ?? 0) || 0;

export const appointmentLines = (services?: any[]): AppointmentLine[] =>
  (Array.isArray(services) ? services : []).map((s: any) => {
    const qty = Math.max(1, num(s?.pivot?.quantity) || 1);
    return {
      serviceId: String(s?.pivot?.service_id ?? s?.id),
      name: s?.name ?? '',
      price: num(s?.pivot?.price ?? s?.price),
      qty,
      // appointment_service.discount_price là giảm trên MỖI đơn vị (cùng quy ước với API POS V2)
      discount: num(s?.pivot?.discount_price) * qty,
    };
  });

export const linesTotal = (lines: AppointmentLine[]) => lines.reduce((sum, l) => sum + Math.max(0, l.price * l.qty - l.discount), 0);

export interface AppointmentDiff {
  appointmentTotal: number;
  posTotal: number;
  /** posTotal − appointmentTotal: âm = khách trả ít hơn lịch hẹn */
  diff: number;
  /** Phần giảm thêm tại quầy (KM, CK, voucher) so với giảm đã có trên lịch hẹn */
  extraDiscount: number;
  added: { name: string; qty: number; amount: number }[];
  removed: { name: string; qty: number; amount: number }[];
  changed: { name: string; from: string; to: string }[];
}

type LineLike = Pick<CartLine, 'name' | 'qty' | 'listPrice' | 'aptServiceId'>;

const fmt = (n: number) => `${new Intl.NumberFormat('vi-VN').format(n)}đ`;

export const compareWithAppointment = (
  apt: Pick<AppointmentSnapshot, 'lines' | 'total'>,
  lines: LineLike[],
  totals: { subtotal: number; total: number },
): AppointmentDiff => {
  const fromApt = new Map(apt.lines.map(l => [l.serviceId, l]));
  const kept = new Set<string>();
  const added: AppointmentDiff['added'] = [];
  const changed: AppointmentDiff['changed'] = [];
  let keptAptDiscount = 0;

  lines.forEach(l => {
    const src = l.aptServiceId ? fromApt.get(l.aptServiceId) : undefined;
    if (!src || kept.has(src.serviceId)) {
      added.push({ name: l.name, qty: l.qty, amount: l.listPrice * l.qty });
      return;
    }
    kept.add(src.serviceId);
    keptAptDiscount += src.discount || 0;
    if (src.qty !== l.qty) changed.push({ name: l.name, from: `SL ${src.qty}`, to: `SL ${l.qty}` });
    if (src.price !== l.listPrice) changed.push({ name: l.name, from: fmt(src.price), to: fmt(l.listPrice) });
  });

  const removed = apt.lines
    .filter(l => !kept.has(l.serviceId))
    .map(l => ({ name: l.name, qty: l.qty, amount: Math.max(0, l.price * l.qty - (l.discount || 0)) }));

  const totalDiscount = Math.max(0, totals.subtotal - totals.total);
  return {
    appointmentTotal: apt.total,
    posTotal: totals.total,
    diff: totals.total - apt.total,
    extraDiscount: Math.max(0, totalDiscount - keptAptDiscount),
    added,
    removed,
    changed,
  };
};

/**
 * Danh sách lịch hẹn S7 chờ thanh toán (API POS V2 – server đã loại lịch hẹn đã tạo đơn).
 * Tự làm mới mỗi 30 giây và khi quay lại tab.
 */
export function useWaitingAppointments() {
  const [items, setItems] = useState<WaitingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const busy = useRef(false);

  const load = useCallback(async (manual = false) => {
    if (busy.current) return;
    busy.current = true;
    // Bấm Tải lại: nút xoay, giữ tối thiểu 500ms để người dùng thấy đã bấm được.
    // Tự làm mới (30 giây / quay lại tab) thì tải ngầm – không nháy "Đang tải…", không chờ thêm
    if (manual) setLoading(true);
    const started = Date.now();
    try {
      const res = await getPosWaitingOrders();
      if (manual) await new Promise(r => setTimeout(r, Math.max(0, 500 - (Date.now() - started))));
      if (!res.ok) {
        setError(res.message || 'Không tải được danh sách');
        return;
      }
      setItems((res.data || []).map(toWaiting).sort((a, b) => b.waitingSince.localeCompare(a.waitingSince)));
      setError('');
      setLastLoaded(new Date());
    } finally {
      busy.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const reload = useCallback(() => load(true), [load]);
  return { items, loading, error, lastLoaded, reload };
}
